import { format, startOfWeek, addDays, isSameDay } from "date-fns";
import { vi } from "date-fns/locale";
import { cn } from "../../../lib/utils";
import {
  ClinicScheduleStatusDto,
  CreateScheduleSlotCommand,
  CreateScheduleSlotDoctorItem,
  DoctorDto,
  ScheduleSlotDto,
  SlotStatus,
  TimeRangeSlotDto,
} from "../../../web-api-client";
import React, { useState } from "react";
import { Moon, Sun } from "lucide-react";
import { TimeSlotCell } from "./TimeSlotCell";
import { DoctorItem } from "./DoctorItem";
import { AddDoctorsToTimeRangeDialog } from "./AddDoctorsToTimeRangeDialog";

import {
  DndContext,
  PointerSensor,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { toUserMessage } from "../../../utils/toUserMessage";
import { toast } from "../../../hooks/use-toast";

export type AddDoctorPayload = Array<{ doctorId: string; quota: number }>;

interface WeekOverviewProps {
  currentDate: Date;
  doctors: DoctorDto[]; // danh sách để render doctor hiện có trong slot
  doctorSuggestions: DoctorDto[]; // danh sách search từ API
  isDoctorSearchLoading?: boolean;
  onDoctorSearchChange?: (query: string) => void;

  rooms: ClinicScheduleStatusDto[];
  slots: TimeRangeSlotDto[];
  onDayClick: (date: Date, clinicExtId: number) => void;
  onCreateSlot: (data: CreateScheduleSlotCommand) => Promise<void>;
  onUpdateTimeRange: (
    slot: TimeRangeSlotDto,
    next: { start: string; end: string },
  ) => Promise<number | undefined>;
  handleUpdateDoctorSlot: (
    nextCap?: number,
    slot?: ScheduleSlotDto,
    status?: "O" | "C",
  ) => Promise<{ id: number; capacity: number; status: "O" | "C" } | undefined>;
  onChangeTimeRangeStatus: (
    timeRange: TimeRangeSlotDto,
    status: "O" | "C",
  ) => Promise<void>;
  handleDeleteDoctorSlot: (slot: ScheduleSlotDto) => Promise<void>;
  handleDeleteTimeRange: (timeRange: TimeRangeSlotDto) => Promise<void>;
}

type DropCellData = { clinicId: number; dateISO: string };

function buildCellId(clinicId: number, dateISO: string, session: "m" | "a") {
  return `cell:${clinicId}:${dateISO}:${session}`;
}

function parseCellId(id: string) {
  const parts = id.split(":"); // cell, clinicId, dateISO, session
  if (parts.length !== 4) return null;
  const clinicId = Number(parts[1]);
  const dateISO = parts[2];
  const session = parts[3] as "m" | "a";
  if (!clinicId || !dateISO || (session !== "m" && session !== "a"))
    return null;
  return { clinicId, dateISO, session };
}

function DroppableWrapper({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "relative min-h-[80px]",
        isOver && "ring-2 ring-primary ring-offset-2 rounded-md",
      )}
    >
      {children}
    </div>
  );
}

function DraggableWrapper({
  slot,
  children,
}: {
  slot: TimeRangeSlotDto;
  children: React.ReactNode;
}) {
  // id phải unique theo slot
  const dragId = `slot:${slot.clinicExtId}:${slot.startTime?.toString()}:${slot.endTime?.toString()}`;

  const { setNodeRef, attributes, listeners, transform, isDragging } =
    useDraggable({
      id: dragId,
      data: { slot },
    });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.6 : 1,
    cursor: "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()} // tránh click cell khi drag
    >
      {children}
    </div>
  );
}

export default function WeekOverview({
  currentDate,
  doctors,
  doctorSuggestions,
  isDoctorSearchLoading,
  onDoctorSearchChange,
  rooms,
  slots,
  onDayClick,
  onCreateSlot: handleCreateSlot,
  onUpdateTimeRange,
  handleUpdateDoctorSlot,
  onChangeTimeRangeStatus,
  handleDeleteDoctorSlot,
  handleDeleteTimeRange,
}: WeekOverviewProps) {
  const [openAddDoctor, setOpenAddDoctor] = useState(false);
  const [activeSlot, setActiveSlot] = useState<TimeRangeSlotDto | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const copySlotToDay = async (
    slot: TimeRangeSlotDto,
    target: DropCellData,
  ) => {
    if (!slot.startTime || !slot.endTime) return;

    // (optional) chặn copy khác phòng
    if ((slot.clinicExtId ?? 0) !== target.clinicId) {
      toast({
        variant: "destructive",
        title: "Không hợp lệ",
        description: "Chỉ được copy trong cùng phòng khám.",
        duration: 2500,
      });
      return;
    }

    const data = new CreateScheduleSlotCommand();
    data.clinicExtId = slot.clinicExtId!;
    data.dates = [format(new Date(target.dateISO), "dd/MM/yyyy")];
    data.startTime = format(slot.startTime, "HH:mm");
    data.endTime = format(slot.endTime, "HH:mm");

    // copy bác sĩ + quota
    data.doctors = (slot.doctors ?? []).map((d: ScheduleSlotDto) => {
      const quota = (d as any).quota ?? d.capacity ?? 10;
      return new CreateScheduleSlotDoctorItem({
        doctorExtId: d.doctorExtId!,
        quota,
      });
    });

    try {
      await handleCreateSlot(data);
    } catch (err: any) {
      const msg = await toUserMessage(err);
      toast({
        variant: "destructive",
        title: "Copy thất bại",
        description: msg,
        duration: 3000,
      });
    }
  };

  const onDragEnd = async (e: DragEndEvent) => {
    const dragSlot = (e.active.data.current as any)?.slot as
      | TimeRangeSlotDto
      | undefined;
    const overId = e.over?.id?.toString();
    if (!dragSlot || !overId) return;

    const target = parseCellId(overId);
    if (!target) return;

    await copySlotToDay(dragSlot, target);
  };

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));

  const getAppointmentsForCell = (
    roomId: number,
    date: Date,
  ): TimeRangeSlotDto[] => {
    return slots.filter(
      (slot) =>
        slot.clinicExtId === roomId && isSameDay(slot.startTime ?? "", date),
    );
  };

  const getDoctorById = (doctorId?: string) => {
    return doctors.find((d) => d.doctorId === doctorId);
  };

  const today = new Date();

  const handleAddDoctorsToTimeRange = async (
    slot: TimeRangeSlotDto,
    payload: AddDoctorPayload,
  ) => {
    const data = new CreateScheduleSlotCommand();
    data.clinicExtId = slot.clinicExtId!;
    data.dates = [format(slot.startTime!, "dd/MM/yyyy")];
    data.startTime = format(slot.startTime!, "HH:mm");
    data.endTime = format(slot.endTime!, "HH:mm");
    data.doctors = payload.map(
      (x) =>
        new CreateScheduleSlotDoctorItem({
          doctorExtId: x.doctorId,
          quota: x.quota,
        }),
    );

    await handleCreateSlot(data);
    setOpenAddDoctor(false);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="flex-1 md:px-4 pb-6 overflow-auto md:overflow-visible">
        <div className="min-w-[900px]">
          <table className="w-full border-collapse table-fixed min-w-[1500px]">
            <thead>
              <tr className="border-b border-primary  bg-green-50">
                <th className=" p-3 z-[10] text-left w-48 font-semibold md:left-[82px] z-10 border-r"></th>
                <th className="w-24 border-r"></th>
                {weekDays.map((day, index) => (
                  <th
                    key={index}
                    className={cn("p-3 text-center font-semibold border-r")}
                  >
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 font-medium mb-1 uppercase tracking-wide">
                        {
                          format(day, "EEE", {
                            locale: vi,
                          }) /* T2, T3, T4 ... */
                        }
                      </span>
                      <span
                        className={cn(
                          "text-sm font-bold text-gray-800",
                          isSameDay(day, today) && "text-red-500 font-bold",
                        )}
                      >
                        {format(day, "dd/MM", { locale: vi })} {isSameDay(day, today) && "(Hôm nay)"}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rooms.map((room) => {
                // chuẩn bị data cho cả tuần, để reuse cho 2 dòng sáng/chiều
                const perDay = weekDays.map((day) => {
                  const daySlots = getAppointmentsForCell(
                    room.clinicId ?? 0,
                    day,
                  );

                  const morningSlots = daySlots.filter(
                    (slot: TimeRangeSlotDto) => {
                      if (!slot.startTime) return false;
                      const hour = parseInt(format(slot.startTime, "HH"), 10);
                      return hour < 12;
                    },
                  );

                  const afternoonSlots = daySlots.filter(
                    (slot: TimeRangeSlotDto) => {
                      if (!slot.startTime) return false;
                      const hour = parseInt(format(slot.startTime, "HH"), 10);
                      return hour >= 12;
                    },
                  );

                  return { day, daySlots, morningSlots, afternoonSlots };
                });

                return (
                  <React.Fragment key={room.clinicId}>
                    {/* ====== ROW SÁNG ====== */}
                    <tr className="border-b border-primary border-b-gray-300 hover:bg-muted/20 transition-colors">
                      <td
                        className="align-middle p-3 bg-white md:left-[82px] bg-background z-10 border-r align-top"
                        rowSpan={2}
                      >
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm">
                            {room.clinicName}
                          </p>
                        </div>
                      </td>

                      <td className="border-r-2 border-r-gray-300">
                        <div className="flex items-center justify-center gap-2 mt-2">
                          <Sun className="w-4 h-4 fill-current text-yellow-500 " />
                          <p className="text-muted-foreground text-left text-sm font-medium">
                            Sáng
                          </p>
                        </div>
                      </td>

                      {perDay.map(({ day, morningSlots }, dayIndex) => {
                        const dateISO = format(day, "yyyy-MM-dd");
                        const dropId = buildCellId(
                          room.clinicId ?? 0,
                          dateISO,
                          "m",
                        );

                        return (
                          <td
                            key={`m-${room.clinicId}-${dayIndex}`}
                            className={cn(
                              "group relative p-3 pt-0 border-r align-top cursor-pointer",
                              morningSlots.length > 0 ? "" : "text-center",
                            )}
                            onClick={() => onDayClick(day, room.clinicId ?? 0)}
                            data-testid={`cell-morning-${room.clinicId}-${dayIndex}`}
                          >
                            <DroppableWrapper id={dropId}>
                              {morningSlots.length > 0 ? (
                                morningSlots.map(
                                  (slot: TimeRangeSlotDto, idx) => {
                                    return (
                                      <DraggableWrapper
                                        key={`m-${room.clinicId}-${dayIndex}-${idx}`}
                                        slot={slot}
                                      >
                                        <TimeSlotCell
                                          slot={slot}
                                          onAddDoctor={() => {
                                            setActiveSlot(slot);
                                            setOpenAddDoctor(true);
                                          }}
                                          onUpdateTimeRange={({ start, end }) =>
                                            onUpdateTimeRange(slot, {
                                              start,
                                              end,
                                            })
                                          }
                                          onChangeTimeRangeStatus={
                                            onChangeTimeRangeStatus
                                          }
                                          onDelete={handleDeleteTimeRange}
                                        >
                                          {(slot.doctors ?? []).map(
                                            (doctorSlot) => (
                                              <DoctorItem
                                                key={
                                                  doctorSlot.id ??
                                                  doctorSlot.doctorExtId
                                                }
                                                doctor={
                                                  getDoctorById(
                                                    doctorSlot.doctorExtId,
                                                  )!
                                                }
                                                slot={doctorSlot}
                                                onUpdateCapacity={(nextCap) =>
                                                  handleUpdateDoctorSlot(
                                                    nextCap,
                                                    doctorSlot,
                                                    undefined,
                                                  )
                                                }
                                                onStatusChange={(nextStatus) =>
                                                  handleUpdateDoctorSlot(
                                                    undefined,
                                                    doctorSlot,
                                                    nextStatus,
                                                  )
                                                }
                                                onDelete={
                                                  handleDeleteDoctorSlot
                                                }
                                              />
                                            ),
                                          )}
                                        </TimeSlotCell>
                                      </DraggableWrapper>
                                    );
                                  },
                                )
                              ) : (
                                <div className="text-xs text-muted-foreground mt-2">
                                  —
                                </div>
                              )}
                            </DroppableWrapper>
                          </td>
                        );
                      })}
                    </tr>

                    {/* ====== ROW CHIỀU ====== */}
                    <tr className="border-b border-primary hover:bg-muted/20 transition-colors">
                      <td className="border-r-2 border-r-gray-300">
                        <div className="flex items-center justify-center gap-2 mt-2">
                          <Moon className="w-4 h-4 fill-current text-blue-300" />
                          <p className="text-muted-foreground text-left text-sm font-medium">
                            Chiều
                          </p>
                        </div>
                      </td>
                      {perDay.map(({ day, afternoonSlots }, dayIndex) => {
                        const dateISO = format(day, "yyyy-MM-dd");
                        const dropId = buildCellId(
                          room.clinicId ?? 0,
                          dateISO,
                          "a",
                        );

                        return (
                          <td
                            key={`a-${room.clinicId}-${dayIndex}`}
                            className={cn(
                              "group relative p-3 pt-0 border-r align-top cursor-pointer",
                              afternoonSlots.length > 0 ? "" : "text-center",
                            )}
                            onClick={() => onDayClick(day, room.clinicId ?? 0)}
                            data-testid={`cell-afternoon-${room.clinicId}-${dayIndex}`}
                          >
                            <DroppableWrapper id={dropId}>
                              {afternoonSlots.length > 0 ? (
                                afternoonSlots.map((slot, idx) => {
                                  return (
                                    <DraggableWrapper
                                      key={`a-${room.clinicId}-${dayIndex}-${idx}`}
                                      slot={slot}
                                    >
                                      <TimeSlotCell
                                        slot={slot}
                                        onAddDoctor={() => {
                                          setActiveSlot(slot);
                                          setOpenAddDoctor(true);
                                        }}
                                        onUpdateTimeRange={({ start, end }) =>
                                          onUpdateTimeRange(slot, {
                                            start,
                                            end,
                                          })
                                        }
                                        onChangeTimeRangeStatus={
                                          onChangeTimeRangeStatus
                                        }
                                        onDelete={handleDeleteTimeRange}
                                      >
                                        {(slot.doctors ?? []).map(
                                          (doctorSlot) => (
                                            <DoctorItem
                                              key={
                                                doctorSlot.id ??
                                                doctorSlot.doctorExtId
                                              }
                                              doctor={
                                                getDoctorById(
                                                  doctorSlot.doctorExtId,
                                                )!
                                              }
                                              slot={doctorSlot}
                                              onDelete={handleDeleteDoctorSlot}
                                              onUpdateCapacity={(nextCap) =>
                                                handleUpdateDoctorSlot(
                                                  nextCap,
                                                  doctorSlot,
                                                )
                                              }
                                              onStatusChange={(nextStatus) =>
                                                handleUpdateDoctorSlot(
                                                  undefined,
                                                  doctorSlot,
                                                  nextStatus,
                                                )
                                              }
                                            />
                                          ),
                                        )}
                                      </TimeSlotCell>
                                    </DraggableWrapper>
                                  );
                                })
                              ) : (
                                <div className="text-xs text-muted-foreground mt-2">
                                  —
                                </div>
                              )}
                            </DroppableWrapper>
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                );
              })}
              <AddDoctorsToTimeRangeDialog
                open={openAddDoctor}
                onOpenChange={(v) => {
                  setOpenAddDoctor(v);
                  if (!v) {
                    setActiveSlot(null);
                    onDoctorSearchChange?.("");
                  }
                }}
                timeRange={
                  activeSlot
                    ? `${format(activeSlot.startTime ?? "", "HH:mm")} - ${format(activeSlot.endTime ?? "", "HH:mm")}`
                    : ""
                }
                doctorSuggestions={doctorSuggestions}
                isDoctorLoading={isDoctorSearchLoading}
                onDoctorSearchChange={onDoctorSearchChange}
                defaultQuota={10}
                onConfirm={(payload) => {
                  if (!activeSlot) return;
                  handleAddDoctorsToTimeRange(activeSlot, payload);
                }}
              />
            </tbody>
          </table>
        </div>
      </div>
    </DndContext>
  );
}
